/**
 * The four character stats, declared once.
 *
 * Every number a stat moves is derived here or named in a field of another contract, so a stat's
 * meaning is readable in one place rather than scattered through the systems that consult it.
 *
 * Baseline is 5 and the usable range is 1 to 10. Baseline is deliberately the identity: a character
 * with straight fives takes and deals exactly what the tool says, and every derived value reads as
 * a deviation from that rather than as a magic constant.
 */

/**
 * Every stat in the game. The union stays explicit rather than derived from the block, so a stat
 * added to one and not the other fails to compile.
 */
export type StatId = 'strength' | 'perception' | 'endurance' | 'agility';

export type StatBlock = Readonly<Record<StatId, number>>;

export const BASELINE_STAT = 5;
export const MIN_STAT = 1;
export const MAX_STAT = 10;

/** Iteration order for anything that walks the stats, so two runs agree. */
export const STAT_ORDER: readonly StatId[] = ['strength', 'perception', 'endurance', 'agility'];

/** A character with no training and no gift, and the defender every unit test starts from. */
export const BASELINE_STATS: StatBlock = {
  strength: BASELINE_STAT,
  perception: BASELINE_STAT,
  endurance: BASELINE_STAT,
  agility: BASELINE_STAT,
};

/** Health added or removed per point of Endurance away from baseline. */
export const HEALTH_PER_ENDURANCE = 10;

/** Kilos of backpack capacity added or removed per point of Strength away from baseline. */
export const CARRY_KG_PER_STRENGTH = 3;

/** How much longer a bleeding-out character has, per point of Endurance. */
export const BLEEDOUT_PER_ENDURANCE = 0.08;

/** How much a status tick is blunted, per point of Endurance. */
export const STATUS_RESIST_PER_ENDURANCE = 0.05;

/** Fraction of an armour threshold a shooter aims past, per point of Perception. */
export const THRESHOLD_PIERCE_PER_PERCEPTION = 0.02;

/**
 * Clamps a raw stat into the usable range. Stats arrive from saves and level-ups, neither of which
 * this module owns, so nothing downstream assumes they were sane on the way in.
 */
export function clampStat(value: number): number {
  if (!Number.isFinite(value)) return BASELINE_STAT;
  if (value < MIN_STAT) return MIN_STAT;
  if (value > MAX_STAT) return MAX_STAT;
  return value;
}

/** Reads one stat, clamped. The only way the systems below should reach into a block. */
export function statValue(stats: StatBlock, id: StatId): number {
  return clampStat(stats[id]);
}

/**
 * The one scaling shape every stat effect uses: `1 + perPoint * (stat - baseline)`, floored at zero
 * so a large negative coefficient can never flip a multiplier's sign.
 */
export function statScale(stats: StatBlock, id: StatId, perPoint: number): number {
  return Math.max(0, 1 + perPoint * (statValue(stats, id) - BASELINE_STAT));
}

/** Maximum health for a body's base pool at a given Endurance. Never below one point. */
export function maxHealthFor(baseHealth: number, stats: StatBlock): number {
  const endurance = statValue(stats, 'endurance');
  return Math.max(1, baseHealth + HEALTH_PER_ENDURANCE * (endurance - BASELINE_STAT));
}

/** Builds a full block from partial overrides, so an instance file names only what differs. */
export function statsFrom(overrides: Partial<StatBlock>): StatBlock {
  return {
    strength: clampStat(overrides.strength ?? BASELINE_STAT),
    perception: clampStat(overrides.perception ?? BASELINE_STAT),
    endurance: clampStat(overrides.endurance ?? BASELINE_STAT),
    agility: clampStat(overrides.agility ?? BASELINE_STAT),
  };
}
